import { useState } from 'react';
import { ROLE_LABELS } from '@shared/roles';
import type { AdminSessionResponse } from '@shared/types';
import { useAuth } from '../app/auth';
import { api, ApiError, describeError } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Badge, Button, Card, CardHeader, Field, Input, Notice, PageHeader } from '../components/ui';

/** 账号与安全：改密、两步验证。 */
export function AccountPage() {
  const { session, setSession } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [secret, setSecret] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [totpBusy, setTotpBusy] = useState(false);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [disableCode, setDisableCode] = useState('');

  async function changePassword() {
    if (newPassword !== confirm) {
      setError('两次输入的口令不一致');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const next = await api.post<AdminSessionResponse>('/api/auth/password', {
        currentPassword,
        newPassword,
      });
      setSession(next);
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      setNotice('口令已更新，其它设备上的登录已失效');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : describeError(caught));
    } finally {
      setSaving(false);
    }
  }

  async function startTotp() {
    setTotpBusy(true);
    setTotpError(null);
    try {
      const result = await api.post<{ secret: string; otpauthUrl: string }>('/api/auth/totp/setup');
      setSecret(result.secret);
      setOtpauth(result.otpauthUrl);
    } catch (caught) {
      setTotpError(describeError(caught));
    } finally {
      setTotpBusy(false);
    }
  }

  async function enableTotp() {
    setTotpBusy(true);
    setTotpError(null);
    try {
      await api.post('/api/auth/totp/enable', { code: code.trim() });
      setSecret(null);
      setOtpauth(null);
      setCode('');
      setNotice('两步验证已开启');
      await refreshSession();
    } catch (caught) {
      setTotpError(caught instanceof ApiError ? caught.message : describeError(caught));
    } finally {
      setTotpBusy(false);
    }
  }

  async function disableTotp() {
    setTotpBusy(true);
    setTotpError(null);
    try {
      await api.post('/api/auth/totp/disable', { code: disableCode.trim() });
      setDisableCode('');
      setNotice('两步验证已关闭');
      await refreshSession();
    } catch (caught) {
      setTotpError(caught instanceof ApiError ? caught.message : describeError(caught));
    } finally {
      setTotpBusy(false);
    }
  }

  async function refreshSession() {
    const next = await api.get<AdminSessionResponse>('/api/auth/me');
    setSession(next);
  }

  if (!session) return null;

  return (
    <div className="space-y-5">
      <PageHeader title="账号与安全" description="员工账号只用于运营后台，与客户账号无关。" />

      {notice ? <Notice tone="ok">{notice}</Notice> : null}

      <Card>
        <CardHeader title="账号信息" />
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
          <div>
            <dt className="text-fg-muted">姓名</dt>
            <dd className="text-fg">{session.user.name}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">邮箱</dt>
            <dd className="text-fg">{session.user.email}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">角色</dt>
            <dd>
              <Badge tone="brand">{ROLE_LABELS[session.user.role]}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-fg-muted">最近登录</dt>
            <dd className="text-fg">{formatDateTime(session.user.lastLoginAt)}</dd>
          </div>
          <div>
            <dt className="text-fg-muted">会话到期</dt>
            <dd className="text-fg">{formatDateTime(session.sessionsExpireAt)}</dd>
          </div>
        </dl>
      </Card>

      <Card>
        <CardHeader title="修改口令" hint="修改后其它设备上的登录会立即失效" />
        {error ? (
          <div className="mb-3">
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}
        <div className="grid max-w-xl gap-3">
          <Field label="当前口令" htmlFor="current">
            <Input
              id="current"
              type="password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
            />
          </Field>
          <Field label="新口令" htmlFor="next" hint="至少 10 位，包含字母与数字">
            <Input
              id="next"
              type="password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
            />
          </Field>
          <Field label="确认新口令" htmlFor="confirm">
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />
          </Field>
          <div>
            <Button variant="primary" loading={saving} onClick={() => void changePassword()}>
              更新口令
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="两步验证"
          hint="强烈建议开启：后台账号能停用客户、发起退款"
          action={
            <Badge tone={session.user.totpEnabled ? 'ok' : 'warn'}>
              {session.user.totpEnabled ? '已开启' : '未开启'}
            </Badge>
          }
        />

        {totpError ? (
          <div className="mb-3">
            <Notice tone="danger">{totpError}</Notice>
          </div>
        ) : null}

        {session.user.totpEnabled ? (
          <div className="max-w-md space-y-3">
            <Field label="验证码" htmlFor="disable-code" hint="关闭两步验证需要当前验证码">
              <Input
                id="disable-code"
                value={disableCode}
                onChange={(value) => setDisableCode(value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
              />
            </Field>
            <Button variant="danger" loading={totpBusy} onClick={() => void disableTotp()}>
              关闭两步验证
            </Button>
          </div>
        ) : secret ? (
          <div className="max-w-md space-y-3">
            <Notice tone="info">
              在验证器 App 里添加以下密钥（或直接打开 otpauth 链接），然后输入生成的 6 位数字。
            </Notice>
            <p className="break-all rounded-md bg-canvas px-3 py-2 font-mono text-xs text-fg">{secret}</p>
            {otpauth ? (
              <a href={otpauth} className="block break-all text-xs text-brand hover:underline">
                {otpauth}
              </a>
            ) : null}
            <Field label="验证码" htmlFor="enable-code">
              <Input
                id="enable-code"
                value={code}
                onChange={(value) => setCode(value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
              />
            </Field>
            <Button variant="primary" loading={totpBusy} onClick={() => void enableTotp()}>
              确认开启
            </Button>
          </div>
        ) : (
          <Button loading={totpBusy} onClick={() => void startTotp()}>
            开始设置
          </Button>
        )}
      </Card>
    </div>
  );
}
